import React, { useEffect, useRef } from 'react';

type PeekDefinitionProps = {
  isOpen: boolean;
  symbolName: string;
  filePath: string;
  code: string;
  line: number;
  column: number;
  position: { x: number; y: number };
  onClose: () => void;
  onGoToDefinition: () => void;
};

export const PeekDefinition = React.memo(function PeekDefinition({
  isOpen,
  symbolName,
  filePath,
  code,
  line,
  column,
  position,
  onClose,
  onGoToDefinition,
}: PeekDefinitionProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Extract context lines around the definition
  const lines = code.split('\n');
  const startLine = Math.max(0, line - 4);
  const endLine = Math.min(lines.length, line + 10);
  const contextLines = lines.slice(startLine, endLine);

  return (
    <div
      ref={panelRef}
      className="peek-definition-panel"
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 1000,
      }}
    >
      <div className="peek-definition-header">
        <div className="peek-definition-title">
          <span className="peek-symbol">{symbolName}</span>
          <span className="peek-path">{filePath}:{line}</span>
        </div>
        <div className="peek-definition-actions">
          <button
            className="peek-goto-btn"
            onClick={onGoToDefinition}
            title="Go to Definition (Enter)"
          >
            Open
          </button>
          <button
            className="peek-close-btn"
            onClick={onClose}
            title="Close (Escape)"
          >
            &times;
          </button>
        </div>
      </div>
      <div className="peek-definition-code">
        <pre>
          {contextLines.map((lineText, idx) => {
            const lineNum = startLine + idx + 1;
            const isTargetLine = lineNum === line;
            return (
              <div
                key={lineNum}
                className={`peek-line ${isTargetLine ? 'peek-line-highlight' : ''}`}
              >
                <span className="peek-line-number">{lineNum}</span>
                <span className="peek-line-content">{lineText || ' '}</span>
              </div>
            );
          })}
        </pre>
      </div>
    </div>
  );
});
